import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Ubicacion } from './ubicacion';

export interface UbicacionesState {
  list: PaginatedList<Ubicacion>;
  current: Ubicacion;
  actives: Ubicacion[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarUbicacion) => Promise<boolean>;
  actualizar: (id: number, request: ActualizarUbicacion) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarUbicacion {
  nombre: string;
}

export interface ActualizarUbicacion {
  nombre: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useUbicacionesStore = create<UbicacionesState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Ubicacion,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Ubicacion
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Ubicacion>>(
      `/ubicaciones/paginadas?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<Ubicacion>>('/ubicaciones/activas');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<Ubicacion>(`/ubicaciones/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarUbicacion) => {
    const data = await makePost<RegistrarUbicacion, Ubicacion>('/ubicaciones/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Ubicacion
    });

    return true;
  },
  actualizar: async (id: number, request: ActualizarUbicacion) => {
    const data = await makePatch<ActualizarUbicacion, Ubicacion>(`/ubicaciones/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/ubicaciones/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activa: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/ubicaciones/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
